import { injectable } from 'inversify'

import 'reflect-metadata'

type ObjectAccess = {
  [key: string]: unknown
}

type ActionString = string
type ActionArray = [action: ActionString, ...args: unknown[]]
export type ActionObject = {
  action: ActionString
  arguments: unknown[]
}
type Action = ActionArray | ActionString | ActionObject

export const hasProperty = (instance: object, property: string) =>
  Object.prototype.hasOwnProperty.call(instance, property)
export const hasDescriptor = (instance: object, property: string): boolean =>
  !!Object.getOwnPropertyDescriptor(Object.getPrototypeOf(instance), property)

@injectable()
export class AccessControlPolicy {
  static namespace: string = ''

  /**
   * Return do status
   * @param action
   * @returns {boolean}
   * @private
   */
  _do(action: Action) {
    if (!action) {
      console.warn('WARN: You passed an empty action descriptor')
      return false
    }
    const type = typeof action
    const isObject = type === 'object' && !Array.isArray(action)
    const isArray = type === 'object' && Array.isArray(action)
    const isString = type === 'string'

    switch (true) {
      case isString
        && (hasProperty(this, action as ActionString)
          || hasDescriptor(this, action as ActionString)): {
        return this.__invokeRuleByString(action as ActionString)
      }

      case isObject
        && (hasProperty(this, (<ActionObject>action).action as ActionString)
          || hasDescriptor(this, (<ActionObject>action).action as ActionString)): {
        return this.__invokeRuleByObject(action as ActionObject)
      }

      case isArray
        && (hasProperty(this, (<ActionArray>action)[0] as ActionString)
          || hasDescriptor(this, (<ActionArray>action)[0] as ActionString)): {
        return this.__invokeRuleByArray(action as ActionArray)
      }

      default: {
        return false
      }
    }
  }

  __invokeRule(rule: string, args: unknown[]) {
    const thisAsObject = this as unknown as ObjectAccess
    return typeof thisAsObject[rule] === 'function'
      ? thisAsObject[rule](...args)
      : thisAsObject[rule]
  }

  __invokeRuleByString(action: ActionString) {
    const thisAsObject = this as unknown as ObjectAccess
    return typeof thisAsObject[action] === 'function'
      ? thisAsObject[action]()
      : thisAsObject[action]
  }

  __invokeRuleByObject(action: ActionObject) {
    const { action: rule, arguments: args } = action
    return this.__invokeRule(rule, args)
  }

  __invokeRuleByArray(action: ActionArray) {
    const [rule, ...args] = action
    return this.__invokeRule(rule, args)
  }

  /**
   * Check user permission
   * @param [actions]
   * @returns {boolean} user permission
   */
  can(...actions: Action[]): boolean {
    return actions.every(a => this._do(a))
  }

  canNot(...actions: Action[]): boolean {
    return actions.every(a => !this._do(a))
  }

  /**
   * Check user do one of permission
   * @param actions
   * @returns {boolean}
   */
  canSome(...actions: Action[]): boolean {
    return actions.some(a => this._do(a))
  }

  /**
   * Check user do one of permission
   * @param actions
   * @returns {boolean}
   */
  canNotSome(...actions: Action[]): boolean {
    return actions.some(a => !this._do(a))
  }

  /**
   * Async check user permissions
   * @param actions<Array>
   * @returns {Promise<unknown>}
   */
  asyncCan(...actions: Action[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.can(...actions)) {
        return resolve(true)
      }
      reject(new Error(`One of action ${actions.join(', ')}  is denied`))
    })
  }
}
